import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';
import { ErrorService } from '../shared/services/error.service';

export interface CardOperatorTransactionPagedResult {
  items: any[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class CardOperatorService {

  private financialUrl(path = ''): string {
    return buildFinancialApiUrl('card-operator', path);
  }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  getTransactionsByOperator(operatorName: string, start?: string, end?: string, options?: PaginationOptions): Observable<CardOperatorTransactionPagedResult> {
    let params = new HttpParams();
    if (start) params = params.set('start', start);
    if (end) params = params.set('end', end);
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/transactions/operator/${encodeURIComponent(operatorName)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar transações da operadora');
        return throwError(() => err);
      })
    );
  }

  getUnreconciledTransactions(options?: PaginationOptions): Observable<CardOperatorTransactionPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl('/transactions/unreconciled'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar transações não conciliadas');
        return throwError(() => err);
      })
    );
  }

  getAllTransactions(): Observable<any[]> {
    return this.http.get<any[]>(this.financialUrl('/transactions')).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar transações');
        return throwError(() => err);
      })
    );
  }

  getTransactionById(id: number): Observable<any> {
    return this.http.get<any>(this.financialUrl(`/transactions/${id}`)).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar transação');
        return throwError(() => err);
      })
    );
  }

  createTransaction(payload: any): Observable<any> {
    return this.http.post<any>(this.financialUrl('/transactions'), payload).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar transação');
        return throwError(() => err);
      })
    );
  }

  updateTransaction(id: number, payload: any): Observable<any> {
    return this.http.put<any>(this.financialUrl(`/transactions/${id}`), payload).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar transação');
        return throwError(() => err);
      })
    );
  }

  deleteTransaction(id: number): Observable<void> {
    return this.http.delete<void>(this.financialUrl(`/transactions/${id}`)).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao deletar transação');
        return throwError(() => err);
      })
    );
  }

  private mapPagedResponse(response: any): CardOperatorTransactionPagedResult {
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

    const items = rawItems;

    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages,
    };
  }
}
